import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import DropDown from 'src/components/common/field/DropDown';
import Record from 'src/components/report/sales/Record';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import BreadcrumbsCustom from 'src/components/common/BreadcrumbsCustom';
import InputDatePickerRange from 'src/components/common/field/InputDatePickerRange';

import { FieldTableType } from 'src/components/common/table/TypeCustom';
import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import Pagin, { PaginationType } from 'src/components/common/Pagin';
import { useNotify } from 'src/context/ContextNotify';
import { getField } from 'src/common/helper';
import dayjs from 'dayjs';

const sxlabelDropdown = {
  '&.Mui-focused': { color: '#AAB2BD', transform: 'translate(14px, 8px) scale(1)' },
  '&.MuiInputLabel-root': { color: '#AAB2BD', transform: 'translate(14px, 8px) scale(1)' },
};

const sizeDropdown = {
  width: '160px',
  height: '32px'
};

const currentMonth = dayjs().format('YYYY-MM');
const currentDate = dayjs().format('YYYY-MM-DD');

const defaultFilter = {
  doctorid: null,
  dates: currentDate,
  month: null
};

const fieldsDefine: FieldTableType[] = [
  {
    label: '施術名',
    field: 'surgery',
    align: 'left',
    width: '80%'
  },
  // {
  //   label: '単価',
  //   field: 'no',
  //   align: 'left',
  //   width: '10%'
  // },
  {
    label: '数量',
    field: 'qty',
    align: 'left',
    width: '20%'
  },
  // {
  //   label: '合計金額',
  //   field: 'no',
  //   align: 'left',
  //   width: '10%'
  // }
];

const breadcrumbsDefine = [
  {
    to: '/',
    label: '会計'
  },
  {
    label: '売上分析一覧'
  }
];

const datePickerDefault = {
  startDate: currentDate,
  endDate: currentDate,
};

type DatesPickerType = {
  startDate: string | null;
  endDate: string | null;
};

type BtnMonthType = 'this_month' | 'last_month' | null;
type FilterType = {
  doctorid: number | null;
  dates: string | null;
  month: string | null;
};

const Sales = () => {
  const notifyContext = useNotify();

  const [filter, setFilter] = useState<FilterType>(defaultFilter);
  const [datePicker, setDatePicker] = useState<DatesPickerType>(datePickerDefault);

  const [dataTable, setDataTable] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationType | undefined>();

  const [doctorData, setDoctorData] = useState<any[]>([]);
  const [monthData, setMonthData] = useState<any[]>([]);

  const [loadingDataTable, setLoadingDataTable] = useState(false);

  const [buttonSearchClick, setButtonSearchClick] = useState<BtnMonthType>(null);

  const handlePageChange = (eventL: any, value: number) => {

  };

  const handleClickButtonMonth = (type: BtnMonthType) => {
    switch (type) {
      case 'this_month':
        handleChangeFilter('month')(currentMonth);
        break;
      case 'last_month':
        if (monthData.length !== 0) {
          const lastMonth = getField(monthData[1], 'id', null);
          handleChangeFilter('month')(lastMonth);
        }
        break;
      default:
        break;
    }
    setButtonSearchClick(type);
  };

  const handleSearch = () => {
    getList();
  };

  const handleChangeDateRange = (
    startDate: string | null,
    endDate: string | null
  ) => {
    setDatePicker((prevState) => ({
      ...prevState,
      startDate: startDate,
      endDate: endDate,
    }));

    if (startDate !== null && endDate !== null && filter.month !== null && filter.month !== '') {
      handleChangeFilter('month')('');
    }

    const dates = startDate !== null && endDate !== null ?
      (startDate === endDate ? startDate : `${startDate} ~ ${endDate}`) : '';

    handleChangeFilter('dates')(dates);
  };

  const handleChangeFilter = (name: string) => (id: number | string) => {
    let reset = {};
    if (name === 'paymentmethod' && Number(id) !== 2) {
      reset = {
        paymentmethod2: null
      };
    }

    if (name === 'month' && id !== '') {
      handleChangeDateRange(null, null);
      handleChangeFilter('dates')('');
    }

    setFilter(prevState => ({
      ...prevState,
      ...reset,
      [name]: id !== '' ? id : null
    }))
  };

  // const convertItemType = (data: any[], id: string, name: string) => {
  //   const converted = data
  //     ? data.map((item: any) => ({
  //       id: String(item[id]),
  //       name: item[name],
  //     }))
  //     : [];

  //   return converted;
  // };

  const repairURLSearch = () => {
    const url = Object.entries(filter)
      .filter(([_, value]) => value !== null)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return url.length !== 0 ? url + '&' : url;
  };

  const getList = async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setLoadingDataTable(true);

      const URLSearch = repairURLSearch();

      getAPI(`/report/sale?${URLSearch}token=${token}`)
        .then((res: any) => {
          if (res.code === 200) {
            setDataTable(getField(res.data, 'items', []));
            setPagination(getField(res.data, 'pagination', {}));
            setDoctorData(getField(res.data, 'doctors', []));
            setMonthData(getField(res.data, 'months', []));
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: getField(res, 'msg', ''),
              type: 'error'
            });
          }
        })
        .finally(() => {
          setLoadingDataTable(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (buttonSearchClick !== null) {
      getList();
      setButtonSearchClick(null);
    }

    // eslint-disable-next-line
  }, [buttonSearchClick]);

  useEffect(() => {
    getList();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <BreadcrumbsCustom breadcrumbs={breadcrumbsDefine} />
      <Typography
        component='label'
        sx={{
          fontWeight: '500',
          fontSize: '20px',
          lineHeight: '24px'
        }}
      >
        {'売上分析'}
      </Typography>
      <Stack
        direction={'row'}
        spacing='8px'
        sx={{
          mt: '18px'
        }}
      >
        <Stack
          direction={'row'}
          spacing='8px'
        >
          {!loadingDataTable ? (
            <DropDown
              selected={Number(filter.doctorid)}
              idSelect='doctor-id-select'
              defaultSelect={'選択する'}
              placeholder='医師'
              items={doctorData}
              onChange={handleChangeFilter('doctorid')}
              sxInput={sizeDropdown}
              sxLabel={sxlabelDropdown}
              sxMenuProps={{
                width: sizeDropdown.width
              }}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <DropDown
              selected={filter.month}
              idSelect='month-id-select'
              defaultSelect={'選択する'}
              placeholder='2024年7月'
              items={monthData}
              onChange={handleChangeFilter('month')}
              sxInput={sizeDropdown}
              sxLabel={sxlabelDropdown}
              svgIconUrl='/images/ic-date-picker.svg'
              sxMenuProps={{
                width: sizeDropdown.width
              }}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <InputDatePickerRange
              startDate={datePicker.startDate}
              endDate={datePicker.endDate}
              placeholder='期間'
              sxInput={{ ...sizeDropdown, width: '240px' }}
              onChange={handleChangeDateRange}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'240px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <BtnGradient
              text='検索'
              handleButtonClick={handleSearch}
              sx={{
                minWidth: '60px',
                width: '60px',
              }}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'60px'} height={'32px'} />
            </Box>
          )}
        </Stack>

        <Stack
          direction={'row'}
          justifyContent={'right'}
          width={'100%'}
          spacing={'8px'}
        >
          {!loadingDataTable ? (
            <BtnOutlineGradient
              text='今月'
              handleButtonClick={() => handleClickButtonMonth('this_month')}
              sx={{
                width: '80px'
              }}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'80px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <BtnOutlineGradient
              text='先月'
              handleButtonClick={() => handleClickButtonMonth('last_month')}
              sx={{
                width: '80px'
              }}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'80px'} height={'32px'} />
            </Box>
          )}
        </Stack>
      </Stack>

      <Box sx={{ mt: '27px' }}>
        {!loadingDataTable ? (
          <Record
            items={dataTable}
            fields={fieldsDefine}
          />
        ) : (
          <Skeleton variant='rounded' width={'100%'} height={'400px'} />
        )}
      </Box>

      <Box sx={{ width: '100%' }} display={'flex'} justifyContent={'center'}>
        {dataTable.length > 0 && pagination && pagination.page && (
          <Pagin handlePageChange={handlePageChange} pagination={pagination} />
        )}
      </Box>
    </Box>
  );
}

export default Sales;